import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Building2, Megaphone, ExternalLink } from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';
import ShareButtons from '@/components/ShareButtons';

const API = process.env.REACT_APP_BACKEND_URL;
const ORIGIN = typeof window !== 'undefined' ? window.location.origin : '';

const AnnouncementPage = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [announcement, setAnnouncement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/partners/announcements/${slug}`)
      .then(r => {
        if (!r.ok) throw new Error('Not found');
        return r.json();
      })
      .then(data => {
        setAnnouncement(data.announcement);
        // If accessed by id but slug exists, redirect to clean URL
        if (data.announcement.slug && data.announcement.slug !== slug) {
          navigate(`/media/${data.announcement.slug}`, { replace: true });
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug, navigate]);

  // Set OG meta tags
  useEffect(() => {
    if (!announcement) return;
    document.title = `${announcement.title} — Taxera Technologies`;
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', announcement.title);
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) ogDesc.setAttribute('content', announcement.summary || '');
    const ogUrl = document.querySelector('meta[property="og:url"]');
    if (ogUrl) ogUrl.setAttribute('content', `${ORIGIN}/media/${announcement.slug || slug}`);
  }, [announcement, slug]);

  if (loading) {
    return (
      <PageLayout>
        <div className="max-w-3xl mx-auto px-6 py-20">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-slate-100 rounded w-1/4" />
            <div className="h-8 bg-slate-100 rounded w-3/4" />
            <div className="h-4 bg-slate-100 rounded w-full" />
            <div className="h-4 bg-slate-100 rounded w-5/6" />
          </div>
        </div>
      </PageLayout>
    );
  }

  if (notFound || !announcement) {
    return (
      <PageLayout>
        <div className="max-w-3xl mx-auto px-6 py-20 text-center">
          <Megaphone className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h1 className="font-heading text-2xl font-bold text-taxera-charcoal mb-2">Announcement not found</h1>
          <p className="text-slate-500 mb-6">This announcement may have been removed or doesn't exist.</p>
          <Link to="/media" className="text-taxera-blue font-semibold hover:underline">
            Back to Media
          </Link>
        </div>
      </PageLayout>
    );
  }

  const pageUrl = `${ORIGIN}/media/${announcement.slug || slug}`;

  return (
    <PageLayout>
      <article className="max-w-3xl mx-auto px-6 py-8" data-testid="announcement-page">
        {/* Back link */}
        <Link
          to="/media"
          className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-taxera-blue transition-colors mb-8"
          data-testid="back-to-media"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          All announcements
        </Link>

        {/* Header image */}
        {announcement.image_url && (
          <div className="relative rounded-2xl overflow-hidden mb-8 aspect-[2/1]">
            <img src={announcement.image_url} alt={announcement.title} className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
          </div>
        )}

        {/* Meta */}
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          {announcement.tier && (
            <span className="text-2xs font-bold uppercase tracking-wide text-white bg-taxera-blue px-2.5 py-1 rounded-full">
              {announcement.tier}
            </span>
          )}
          {(announcement.published_at || announcement.date) && (
            <span className="text-xs text-slate-400 flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" />
              {new Date(announcement.published_at || announcement.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
          )}
        </div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          className="font-heading text-3xl sm:text-4xl font-bold text-taxera-charcoal leading-tight mb-4"
          data-testid="announcement-title"
        >
          {announcement.title}
        </motion.h1>

        {/* Partner */}
        {announcement.partner_name && (
          <div className="flex items-center gap-2 text-sm text-taxera-teal font-semibold mb-6">
            <Building2 className="w-4 h-4" />
            Strategic partnership with {announcement.partner_name}
          </div>
        )}

        {/* Share */}
        <div className="flex items-center gap-3 pb-6 mb-8 border-b border-slate-100">
          <span className="text-xs text-slate-400 font-medium">Share:</span>
          <ShareButtons url={pageUrl} title={announcement.title} summary={announcement.summary} compact />
        </div>

        {/* Summary */}
        {announcement.summary && (
          <p className="text-lg text-slate-700 leading-relaxed mb-8 font-medium">
            {announcement.summary}
          </p>
        )}

        {/* Full content */}
        {announcement.content && (
          <div className="prose prose-slate max-w-none space-y-5" data-testid="announcement-content">
            {announcement.content.split('\n\n').map((para, j) => {
              if (para.startsWith('\u201C') || para.startsWith('"')) {
                const dashIdx = para.lastIndexOf('\u2014');
                const quoteText = dashIdx > 0 ? para.substring(0, dashIdx).trim() : para;
                const attribution = dashIdx > 0 ? para.substring(dashIdx) : '';
                return (
                  <blockquote key={`q-${para.slice(0, 30)}`} className="border-l-4 border-taxera-teal pl-5 py-3 bg-taxera-teal/[0.03] rounded-r-xl my-6">
                    <p className="text-slate-600 italic leading-relaxed text-base">{quoteText}</p>
                    {attribution && (
                      <p className="text-sm font-semibold text-taxera-charcoal mt-2 not-italic">{attribution}</p>
                    )}
                  </blockquote>
                );
              }
              return <p key={`p-${para.slice(0, 30)}`} className="text-base text-slate-700 leading-relaxed">{para}</p>;
            })}
          </div>
        )}

        {/* Footer */}
        <div className="mt-10 pt-6 border-t border-slate-100 space-y-4">
          {announcement.source_url && (
            <a
              href={announcement.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-semibold text-taxera-charcoal hover:bg-taxera-blue/5 hover:border-taxera-blue/30 hover:text-taxera-blue transition-all"
              data-testid="partner-url-link"
            >
              <ExternalLink className="w-4 h-4" />
              Partner press release
            </a>
          )}
          {announcement.announced_by && (
            <p className="text-sm text-slate-500">
              Announced by <span className="font-semibold text-taxera-charcoal">{announcement.announced_by}</span>
            </p>
          )}
        </div>

        {/* Back CTA */}
        <div className="mt-10 pt-6 border-t border-slate-100 flex items-center justify-between">
          <Link to="/media" className="text-sm text-taxera-blue font-semibold hover:underline flex items-center gap-1.5">
            <ArrowLeft className="w-3.5 h-3.5" /> All announcements
          </Link>
          <Link to="/partners" className="text-sm text-slate-500 hover:text-taxera-blue transition-colors">
            Explore Partner Program &rarr;
          </Link>
        </div>
      </article>
    </PageLayout>
  );
};

export default AnnouncementPage;
